import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

const ParkContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ParkProvider = ({ children }) => {
  const [parks, setParks] = useState([]);
  const [selectedPark, setSelectedPark] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const lastTokenRef = useRef(null);

  const fetchParks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/parks`);
      const newParks = response.data;
      setParks(newParks);
      
      // Update selected park if needed
      if (newParks.length > 0) {
        setSelectedPark(current => {
          const currentStillExists = current && newParks.find(p => p.id === current.id);
          return currentStillExists ? current : newParks[0];
        });
      } else {
        setSelectedPark(null);
      }
    } catch (error) {
      console.error('Error fetching parks:', error);
      setParks([]);
      setSelectedPark(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check for token changes and refetch parks
  useEffect(() => {
    const checkToken = () => {
      const currentToken = localStorage.getItem('token');
      if (currentToken !== lastTokenRef.current) {
        lastTokenRef.current = currentToken;
        fetchParks();
      }
    };

    // Initial check
    checkToken();

    // Set up interval to check for token changes
    const interval = setInterval(checkToken, 500);

    return () => clearInterval(interval);
  }, [fetchParks]);

  const createPark = useCallback(async (parkData) => {
    const response = await axios.post(`${API}/parks`, parkData);
    await fetchParks();
    return response.data;
  }, [fetchParks]);

  const updatePark = useCallback(async (parkId, parkData) => {
    const response = await axios.put(`${API}/parks/${parkId}`, parkData);
    await fetchParks();
    return response.data;
  }, [fetchParks]);

  const deletePark = useCallback(async (parkId) => {
    await axios.delete(`${API}/parks/${parkId}`);
    setParks(prev => {
      const remaining = prev.filter(p => p.id !== parkId);
      return remaining;
    });
    setSelectedPark(current => current?.id === parkId ? null : current);
  }, []);

  const contextValue = useMemo(() => ({
    parks,
    selectedPark,
    setSelectedPark,
    selectedYear,
    setSelectedYear,
    loading,
    fetchParks,
    refreshParks: fetchParks,
    createPark,
    updatePark,
    deletePark
  }), [parks, selectedPark, selectedYear, loading, fetchParks, createPark, updatePark, deletePark]);

  return (
    <ParkContext.Provider value={contextValue}>
      {children}
    </ParkContext.Provider>
  );
};

export const usePark = () => {
  const context = useContext(ParkContext);
  if (!context) {
    throw new Error('usePark must be used within a ParkProvider');
  }
  return context;
};
